<script lang="ts">
  import Button from '../common/Button.svelte';
  import type { Transaction } from '../../types/transaction';
  import { sumBy } from '../../lib/utils/array-helpers';

  let {
    transaction,
    categories = [],
    onSplit,
    onCancel
  }: {
    transaction: Transaction;
    categories: string[];
    onSplit: (splits: any[]) => void;
    onCancel: () => void;
  } = $props();

  // Default to 2 splits, evenly divided
  let numSplits = $state(2);

  // Initialize splits function
  function initializeSplits(num: number) {
    const originalAmount = Math.abs(transaction.amount);
    const evenAmount = parseFloat((originalAmount / num).toFixed(2));

    // Initialize splits array
    const newSplits = Array.from({ length: num }, (_, idx) => ({
      id: `${transaction.id}-split-${idx}-${Date.now()}`, // Generate unique ID for each split
      amount: evenAmount,
      category: transaction.category,
      person: transaction.person,
      description: transaction.description,
      displayName: null
    }));

    // Adjust the last split to account for rounding
    const total = sumBy(newSplits.slice(0, -1), s => s.amount);
    newSplits[newSplits.length - 1].amount = parseFloat((originalAmount - total).toFixed(2));

    return newSplits;
  }

  let splits = $state<any[]>(initializeSplits(2));

  // Calculate total and validate
  let total = $derived(sumBy(splits, split => split.amount));
  let originalAmount = $derived(Math.abs(transaction.amount));
  let isValid = $derived(Math.abs(total - originalAmount) < 0.01 && splits.every(s => s.amount > 0));

  function handleNumSplitsChange(num: number) {
    if (num >= 2 && num <= 3) {
      numSplits = num;
      splits = initializeSplits(num);
    }
  }

  function handleSplit() {
    if (isValid) {
      onSplit(splits);
    }
  }

  function redistributeEvenly() {
    splits = initializeSplits(numSplits);
  }
</script>

<div class="modal-backdrop" onclick={onCancel}>
  <div class="modal-content" onclick={(e) => e.stopPropagation()}>
    <div class="modal-header">
      <h2>Split Transaction</h2>
      <button class="close-btn" onclick={onCancel}>&times;</button>
    </div>

    <div class="modal-body">
      <div class="original-info">
        <p><strong>Original Amount:</strong> ${originalAmount.toFixed(2)}</p>
        <p><strong>Description:</strong> {transaction.description}</p>
        <p><strong>Date:</strong> {transaction.date}</p>
      </div>

      <div class="split-controls">
        <label>
          <span>Number of splits:</span>
          <div class="split-buttons">
            <button
              class="split-num-btn"
              class:active={numSplits === 2}
              onclick={() => handleNumSplitsChange(2)}
            >
              2
            </button>
            <button
              class="split-num-btn"
              class:active={numSplits === 3}
              onclick={() => handleNumSplitsChange(3)}
            >
              3
            </button>
          </div>
        </label>

        <button class="redistribute-btn" onclick={redistributeEvenly}>
          Redistribute Evenly
        </button>
      </div>

      <div class="splits-container">
        {#each splits as split, idx}
          <div class="split-item">
            <h4>Split {idx + 1}</h4>

            <label>
              <span>Amount:</span>
              <input
                type="number"
                step="0.01"
                min="0.01"
                bind:value={split.amount}
              />
            </label>

            <label>
              <span>Category:</span>
              <select bind:value={split.category}>
                {#each categories as cat}
                  <option value={cat}>{cat}</option>
                {/each}
              </select>
            </label>

            <label>
              <span>Person:</span>
              <input type="text" bind:value={split.person} />
            </label>

            <label>
              <span>Description (optional):</span>
              <input type="text" bind:value={split.description} />
            </label>
          </div>
        {/each}
      </div>

      <div class="validation">
        <p class:valid={isValid} class:invalid={!isValid}>
          <strong>Total:</strong> ${total.toFixed(2)} / ${originalAmount.toFixed(2)}
          {#if !isValid}
            {#if Math.abs(total - originalAmount) >= 0.01}
              <span class="error-msg">⚠️ Total must equal original amount</span>
            {/if}
            {#if splits.some(s => s.amount <= 0)}
              <span class="error-msg">⚠️ All amounts must be greater than 0</span>
            {/if}
          {:else}
            <span class="success-msg">✓</span>
          {/if}
        </p>
      </div>
    </div>

    <div class="modal-footer">
      <Button variant="secondary" onclick={onCancel}>Cancel</Button>
      <Button variant="success" disabled={!isValid} onclick={handleSplit}>
        Split Transaction
      </Button>
    </div>
  </div>
</div>

<style lang="scss">
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: var(--z-modal);
    padding: var(--spacing-xl);
  }

  .modal-content {
    background: white;
    border-radius: var(--radius-xl);
    box-shadow: var(--shadow-2xl);
    max-width: 600px;
    width: 100%;
    max-height: 90vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--spacing-xl);
    border-bottom: 1px solid var(--color-border);

    h2 {
      margin: 0;
      font-size: var(--font-size-xl);
      font-weight: var(--font-weight-semibold);
      color: var(--color-text-primary);
    }

    .close-btn {
      background: none;
      border: none;
      font-size: var(--font-size-3xl);
      color: var(--color-text-tertiary);
      cursor: pointer;
      padding: 0;
      width: var(--icon-size-lg);
      height: var(--icon-size-lg);
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: var(--radius-sm);
      transition: all var(--transition-fast);

      &:hover {
        background: var(--color-background-tertiary);
        color: var(--color-text-primary);
      }
    }
  }

  .modal-body {
    padding: var(--spacing-xl);
    overflow-y: auto;
    flex: 1;
  }

  .original-info {
    background: var(--color-background-secondary);
    padding: var(--spacing-md);
    border-radius: var(--radius-md);
    margin-bottom: var(--spacing-xl);
    border: 1px solid var(--color-border);

    p {
      margin: var(--spacing-xs) 0;
      font-size: var(--font-size-sm);
      color: var(--color-text-secondary);

      strong {
        color: var(--color-text-primary);
      }
    }
  }

  .split-controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--spacing-xl);
    padding-bottom: var(--spacing-lg);
    border-bottom: 1px solid var(--color-border);

    label {
      display: flex;
      align-items: center;
      gap: var(--spacing-md);
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-medium);
    }

    .split-buttons {
      display: flex;
      gap: var(--spacing-sm);
    }

    .split-num-btn {
      padding: var(--spacing-sm) var(--spacing-lg);
      border: 2px solid var(--color-border-medium);
      background: white;
      border-radius: var(--radius-md);
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-semibold);
      cursor: pointer;
      transition: all var(--transition-fast);
      color: var(--color-text-secondary);

      &:hover {
        border-color: var(--color-primary-bright);
        background: var(--color-primary-light);
      }

      &.active {
        border-color: var(--color-primary-bright);
        background: var(--color-primary-bright);
        color: white;
      }
    }

    .redistribute-btn {
      padding: var(--spacing-sm) var(--spacing-md);
      border: 1px solid var(--color-border-medium);
      background: white;
      border-radius: var(--radius-md);
      font-size: var(--font-size-sm);
      cursor: pointer;
      transition: all var(--transition-fast);
      color: var(--color-text-secondary);

      &:hover {
        background: var(--color-background-tertiary);
        border-color: var(--color-text-muted);
      }
    }
  }

  .splits-container {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-lg);
    margin-bottom: var(--spacing-lg);
  }

  .split-item {
    background: var(--color-background-secondary);
    padding: var(--spacing-lg);
    border-radius: var(--radius-lg);
    border: 2px solid var(--color-border);

    h4 {
      margin: 0 0 var(--spacing-md) 0;
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-semibold);
      color: var(--color-text-primary);
    }

    label {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xs);
      margin-bottom: var(--spacing-md);

      span {
        font-size: var(--font-size-xs);
        font-weight: var(--font-weight-medium);
        color: var(--color-text-tertiary);
      }

      input, select {
        padding: var(--spacing-sm);
        border: 1px solid var(--color-border-medium);
        border-radius: var(--radius-md);
        font-size: var(--font-size-sm);
        background: white;

        &:focus {
          outline: none;
          border-color: var(--color-primary-bright);
          box-shadow: var(--shadow-focus);
        }
      }
    }
  }

  .validation {
    background: var(--color-background-tertiary);
    padding: var(--spacing-md);
    border-radius: var(--radius-md);
    margin-bottom: var(--spacing-lg);

    p {
      margin: 0;
      font-size: var(--font-size-sm);
      display: flex;
      align-items: center;
      gap: var(--spacing-sm);
      flex-wrap: wrap;

      &.valid {
        color: var(--color-success-hover);
      }

      &.invalid {
        color: var(--color-danger-hover);
      }

      strong {
        font-weight: var(--font-weight-semibold);
      }

      .error-msg {
        font-size: var(--font-size-sm);
        color: var(--color-danger-hover);
      }

      .success-msg {
        color: var(--color-success-hover);
        font-size: var(--font-size-base);
      }
    }
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: var(--spacing-md);
    padding: var(--spacing-lg) var(--spacing-xl);
    border-top: 1px solid var(--color-border);
    background: var(--color-background-secondary);
  }
</style>
